import React, { createContext, useState, useContext, useEffect } from 'react';
import { TranslationContextType, TranslationResult, PhrasebookEntry, HistoryEntry } from '../types';
import { translateText as apiTranslateText } from '../services/translationService';

// Create context with default values
const TranslationContext = createContext<TranslationContextType>({
  sourceLanguage: 'en',
  targetLanguage: 'es',
  setSourceLanguage: () => {},
  setTargetLanguage: () => {},
  swapLanguages: () => {},
  translateText: async () => ({
    sourceText: '',
    translatedText: '',
    sourceLanguage: '',
    targetLanguage: '',
    timestamp: new Date()
  }),
  isTranslating: false,
  history: [],
  phrasebook: [],
  addToPhrasebook: () => {},
  removeFromPhrasebook: () => {}
});

interface TranslationProviderProps {
  children: React.ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('es');
  const [isTranslating, setIsTranslating] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [phrasebook, setPhrasebook] = useState<PhrasebookEntry[]>([]);

  // Load history and phrasebook from localStorage on initial render
  useEffect(() => {
    const savedHistory = localStorage.getItem('translationHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        // Convert string timestamps back to Date objects
        const formattedHistory = parsedHistory.map((entry: any) => ({
          ...entry,
          timestamp: new Date(entry.timestamp)
        }));
        setHistory(formattedHistory);
      } catch (error) {
        console.error('Failed to parse history from localStorage:', error);
      }
    }

    const savedPhrasebook = localStorage.getItem('phrasebook');
    if (savedPhrasebook) {
      try {
        const parsedPhrasebook = JSON.parse(savedPhrasebook);
        // Convert string timestamps back to Date objects
        const formattedPhrasebook = parsedPhrasebook.map((entry: any) => ({
          ...entry,
          createdAt: new Date(entry.createdAt)
        }));
        setPhrasebook(formattedPhrasebook);
      } catch (error) {
        console.error('Failed to parse phrasebook from localStorage:', error);
      }
    }
  }, []);

  // Save history and phrasebook to localStorage when they change
  useEffect(() => {
    localStorage.setItem('translationHistory', JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    localStorage.setItem('phrasebook', JSON.stringify(phrasebook));
  }, [phrasebook]);

  const swapLanguages = () => {
    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
  };

  const translateText = async (text: string): Promise<TranslationResult> => {
    setIsTranslating(true);
    try {
      const result = await apiTranslateText(text, sourceLanguage, targetLanguage);
      
      // Add to history if there's meaningful content
      if (result.sourceText.trim() && result.translatedText.trim()) {
        const historyEntry: HistoryEntry = {
          ...result,
          id: `hist_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        };
        setHistory(prevHistory => [historyEntry, ...prevHistory].slice(0, 100)); // Keep only last 100 entries
      }
      
      return result;
    } catch (error) {
      console.error('Translation error:', error);
      throw error;
    } finally {
      setIsTranslating(false);
    }
  };

  const addToPhrasebook = (entry: Omit<PhrasebookEntry, 'id' | 'createdAt'>) => {
    const newEntry: PhrasebookEntry = {
      ...entry,
      id: `phrase_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      createdAt: new Date()
    };
    setPhrasebook(prevPhrasebook => [newEntry, ...prevPhrasebook]);
  };

  const removeFromPhrasebook = (id: string) => {
    setPhrasebook(prevPhrasebook => prevPhrasebook.filter(entry => entry.id !== id));
  };

  const value: TranslationContextType = {
    sourceLanguage,
    targetLanguage,
    setSourceLanguage,
    setTargetLanguage,
    swapLanguages,
    translateText,
    isTranslating,
    history,
    phrasebook,
    addToPhrasebook,
    removeFromPhrasebook
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);